import { MessageAction, MessageCopyAction, MessageMoreAction, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { Copy, Flag, Library, Link, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { createImportMutation, type SrcSchemasChatMessageResponse } from "@/api";
import { useFeedbackDialog } from "@/features/feedback/FeedbackProvider";
import { messageAnchor } from "./messageLink";
import { type ReplyActionsValue, useReplyActionsContext } from "./replyActionsContext";

/** A link that opens the chat at this reply. */
function linkTo(chat: ReplyActionsValue, uuid: string) {
  return `${globalThis.location.origin}${chat.host.pathFor(chat.sessionUuid)}#${messageAnchor(uuid)}`;
}

/** The import's title in the space's inbox: the reply's first line, shortened. */
function titleOf(text: string) {
  const first =
    text
      .trim()
      .split("\n")[0]
      ?.replace(/^#+\s*/, "") ?? "";
  return first.length > 80 ? `${first.slice(0, 79)}…` : first;
}

/** A reply as text to copy: images are left out (their markdown is only a file name and a link that needs sign-in). */
export function copyableText(content: string): string {
  const text = content
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return text || content;
}

/**
 * The actions under a saved reply (Figma "Action row"): Copy, Try again on the chat's latest reply (not one that made
 * files: replacing it would take them), and More: a link to the reply, Save to knowledge in a space's chat, and
 * Report an issue. Shared chats and other read-only views get Copy and Report an issue.
 */
export function ReplyActions({
  message,
  madeFiles = false,
}: Readonly<{ message: SrcSchemasChatMessageResponse; madeFiles?: boolean }>) {
  const { t } = useTranslation("chat", { keyPrefix: "replyActions" });
  const toast = useToast();
  const feedback = useFeedbackDialog();
  const chat = useReplyActionsContext();
  const save = useMutation({
    ...createImportMutation(),
    // The backend sees an identical import and keeps the first.
    onSuccess: ({ data }) => toast.success(t(data.alreadyImported ? "alreadySaved" : "saved")),
    onError: () => toast.error(t("saveFailed")),
  });
  const link = chat ? linkTo(chat, message.uuid) : undefined;
  const tryAgain = !madeFiles && chat?.tryAgain?.replyUuid === message.uuid ? chat.tryAgain : undefined;
  const copy = async (text: string, done?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (done) toast.success(done);
    } catch (error) {
      toast.error(t("copyFailed"));
      throw error;
    }
  };

  return (
    <>
      <MessageCopyAction
        icon={<Copy />}
        label={t("copy")}
        copiedLabel={t("copied")}
        onCopy={() => copy(copyableText(message.content))}
      />
      {tryAgain && <MessageAction icon={<RefreshCw />} label={t("tryAgain")} onClick={tryAgain.run} />}
      <MessageMoreAction
        label={t("more")}
        items={[
          ...(link
            ? [{ label: t("copyLink"), icon: <Link size={12} />, onSelect: () => void copy(link, t("linkCopied")) }]
            : []),
          ...(chat?.spaceUuid
            ? [
                {
                  label: t("saveToKnowledge"),
                  icon: <Library size={12} />,
                  disabled: save.isPending,
                  onSelect: () =>
                    save.mutate({
                      path: { space_uuid: chat.spaceUuid ?? "" },
                      body: { title: titleOf(message.content) || null, text: message.content },
                    }),
                },
              ]
            : []),
          {
            label: t("report"),
            icon: <Flag size={12} />,
            danger: true,
            onSelect: () => feedback.open({ pageUrl: link }),
          },
        ]}
      />
    </>
  );
}
